import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * "Something@something.something" — the shape the server's EmailStr accepts,
 * which is all a client-side check should claim. Deliverability is the
 * server's answer (CP-16), and a stricter regex here only rejects addresses
 * that work. ⚠ The browser's `type=email` is LOOSER: it lets a dotless domain
 * ("ada@gmail") through, so a form cannot lean on the native check to enforce
 * this — the candidate gate guards its own submit (2026-09-27).
 *
 * ONE copy. It was written out in five places — the CSV importer, three
 * admin dialogs and the workspace settings page — and a sixth was about to be
 * added for the candidate gate. They agreed; what made that worth fixing is
 * that nothing would have said so if one of them had been tightened
 * (2026-09-21).
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The one character limit every email field shares (FEAT-23). 254 is the
 * longest address that can exist, and what the server refuses past — mirror of
 * `EMAIL_MAX_LENGTH` in backend/email_limits.py.
 *
 * Every input that takes ONE address carries `maxLength={EMAIL_MAX_LENGTH}`;
 * `emailInputLimits.test.ts` walks the source and fails on one that does not.
 * ⚠ An input that accepts a PASTED LIST of addresses must NOT: the browser
 * would silently cut the list. Those are checked per address instead
 * (`validateRecipient`).
 */
export const EMAIL_MAX_LENGTH = 254;

/**
 * The custom `fontSize` keys in tailwind.config.ts that are NOT T-shirt sizes.
 * tailwind-merge classifies `text-<x>` as a font size only for `xs` / `sm` /
 * `2xs`-shaped names; anything else it takes for a text COLOUR, so
 * `cn("text-sm", "text-xsm")` would keep both and let the stylesheet order
 * decide. Listing them here keeps "the later font size wins", the rule
 * `cn("text-sm", "text-xsm")` followed before the sizes got names.
 * themeTokens.test.ts checks every key in the config is covered.
 */
export const NON_TSHIRT_FONT_SIZES = ["xsm", "smd", "code"] as const;

const twMerge = extendTailwindMerge({
  extend: { theme: { text: [...NON_TSHIRT_FONT_SIZES] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// How the candidate terminal screens address the candidate: first name only, so
// "Anna Kowalska" greets as "Anna". Shared by BOTH terminal cards (Done +
// time-up) so the two can't drift. Falls back to the whole string when there is
// nothing to split (mononym, or leading whitespace only).
export function firstName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return parts[0] || fullName;
}

// Backend stores naive UTC datetimes (TIMESTAMP WITHOUT TIME ZONE) and serializes
// them without a `Z` suffix. JS would otherwise parse them as local time. Always
// route server-supplied timestamps through this so the displayed time and any
// countdown math stay correct.
export function parseServerDate(value: string): Date {
  return new Date(/[Z+\-]\d{2}:?\d{2}$|Z$/.test(value) ? value : value + "Z");
}

// Always show "5 May 2026, 13:32" — short month name avoids the US/EU
// 12/05/2026 ambiguity that bites mixed-locale audiences.
export function formatTimestamp(value: string): string {
  try {
    return parseServerDate(value).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

// Strip the common Markdown syntax markers from a string so it reads cleanly
// in a single-line preview. NOT a full Markdown parser — just enough to keep
// `**bold**`, `# heading`, `` `code` `` and friends from leaking literal
// asterisks/backticks/hashes into a `line-clamp-1` preview row.
//
// Order mirrors Markdown's own — block structure first, then the inline pass,
// with inline code held out of both. B2 (2026-09-21 browser QA) was the cost
// of getting that backwards: unwrapping `` `*args` `` ahead of the emphasis
// regexes drops a bare `*` into the string, where it pairs with the NEXT real
// marker instead. "Use `*args` and `**kwargs`" previewed as "Use args and
// *kwargs" — the genuine emphasis eaten, the asterisk leaked. A `*` bullet is
// the same bug one level up, so the list markers go before emphasis, not after.
export function stripMarkdown(text: string): string {
  // Inline code is lifted out whole and restored at the end, so nothing
  // inside it is ever read as a marker. The sentinel is NUL-delimited: none
  // of the regexes below can match across it, and a prompt never carries NUL.
  const code: string[] = [];
  return text
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(
      /`([^`]+)`/g,
      (_m, inner: string) => `\u0000${code.push(inner) - 1}\u0000`,
    )
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^>\s+/gm, "") // blockquotes
    .replace(/^[-*+]\s+/gm, "") // unordered lists
    .replace(/^\d+\.\s+/gm, "") // ordered lists
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold **
    .replace(/__([^_]+)__/g, "$1") // bold __
    .replace(/\*([^*]+)\*/g, "$1") // italic *
    .replace(/(^|\s)_([^_]+)_(?=\s|$)/g, "$1$2") // italic _word_ (avoid mid-word)
    // B5 (2026-09-27 pre-ship): a `**` still here after the balanced pass has
    // no partner — the server truncates the preview at 200 characters, which
    // can cut a bold span's closer off. Code is still held out, so `**kwargs`
    // inside backticks is untouched.
    .replace(/\*\*/g, "")
    .replace(/!?\[([^\]]+)\]\([^)]+\)/g, "$1") // links + images
    .replace(/\u0000(\d+)\u0000/g, (_m, i: string) => code[Number(i)]) // code back
    .replace(/\s+/g, " ") // collapse whitespace (incl. newlines)
    .trim();
}

export function formatRelativeTime(value: string): string {
  const t = parseServerDate(value).getTime();
  const diffMs = Date.now() - t;
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

// "—" for null, formatted timestamp otherwise. Used by admin list/detail tables
// where the column may render before a value exists (e.g. submitted_at on an
// in-progress attempt).
export function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return formatTimestamp(value);
}

// "Not graded" for null (Pyodide subprocess hasn't returned yet), `XX%`
// otherwise. Score is stored as a 0..1 fraction server-side.
export function formatScore(value: number | null): string {
  if (value === null) return "Not graded";
  return `${Math.round(value * 100)}%`;
}

// "Xs" under a minute, "Xm" on whole minutes, "Xm Ys" with seconds. Used by
// /admin/<test> attempt durations.
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

// `mm:ss` with zero-padded minutes (minutes may exceed 59 — a 90-minute
// countdown reads "90:00"). Clamps at "00:00". Candidate review countdowns.
export function formatMMSS(ms: number): string {
  if (ms <= 0) return "00:00";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// `mm:ss` under an hour, `h:mm:ss` past 60 minutes. Clamps at "00:00".
export function formatHMS(ms: number): string {
  if (ms <= 0) return "00:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Wall-clock elapsed between two server timestamps. The `formatHMS` clamp
// absorbs negative deltas from sub-second clock skew between the Fly app and
// the Neon write replica.
export function formatElapsed(
  startedAt: string,
  submittedAt: string | null,
  notSubmittedFallback: string,
): string {
  if (!submittedAt) return notSubmittedFallback;
  const start = parseServerDate(startedAt).getTime();
  const end = parseServerDate(submittedAt).getTime();
  return formatHMS(end - start);
}
